import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  requestableFrom,
  transitionBlockers,
  WRITE_ROLES,
  type Engagement,
  type EngagementStage,
  type LifecycleEvidence,
} from '@ai-secure-twin/shared';
import { useAuth } from '../auth/auth';
import { isStale, latestRun, ScoreBadge, useAgentRuns } from './AiSuggestion';
import { api, ApiError } from '../../lib/api';

/** What each requestable move is called, from the stage it leaves. */
function actionLabel(from: EngagementStage, to: EngagementStage): string {
  if (to === 'PLAN_PENDING_APPROVAL') return 'Request active testing plan';
  if (to === 'REPORT_PENDING_APPROVAL') return 'Submit report for approval';
  if (to === 'TRIAGE') return from === 'ACTIVE' ? 'Finish active testing' : 'Move to triage';
  return to;
}

/**
 * What pressing it actually does, in a sentence. The labels alone left people guessing what the
 * difference was, and the difference — whether attack payloads are ever sent — is the one that
 * matters most in this whole lifecycle.
 */
function actionMeaning(from: EngagementStage, to: EngagementStage): string {
  if (to === 'PLAN_PENDING_APPROVAL') {
    return 'Ask an approver to allow active testing: real attack payloads that can change data on the target. Nothing is sent until they approve.';
  }
  if (to === 'REPORT_PENDING_APPROVAL') {
    return 'Generate the report from the decided findings and send it to an approver. It is fixed from that moment.';
  }
  if (to === 'TRIAGE' && from === 'ACTIVE') {
    return 'Active testing is done. Go on to deciding each finding.';
  }
  return 'Passive testing is enough. Skip active testing — it is recorded as skipped — and go on to deciding each finding.';
}

/** What happens next when there is nothing to press. */
const WAITING: Partial<Record<EngagementStage, string>> = {
  DRAFT: 'Define a scope to begin.',
  SCOPE_PENDING_APPROVAL: 'Waiting for an approver to decide the scope.',
  SCOPED: 'Run a passive scan to begin discovery.',
  DISCOVERY: 'Discovery is in progress.',
  PLAN_PENDING_APPROVAL: 'Waiting for an approver to decide the active testing plan.',
  REPORT_PENDING_APPROVAL: 'Waiting for an approver to decide the report.',
  PUBLISHED: 'This engagement is complete.',
  CANCELLED: 'This engagement was cancelled.',
  FAILED: 'This engagement failed.',
};

/**
 * The moves a person can make from here.
 *
 * Each button is disabled by the same `transitionBlockers` the server enforces, and shows its
 * reasons — so a disabled button says what to do about it rather than nothing. The server still
 * decides: it evaluates the rule against data it reads itself, not against what this page holds.
 */
export function NextStep({
  engagement,
  evidence,
}: {
  engagement: Engagement;
  evidence: LifecycleEvidence;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const mayAct = user ? WRITE_ROLES.has(user.role) : false;
  const moves = requestableFrom(engagement.stage);

  /*
   * The agent's view of what to do next, from whichever of its tasks last produced one. Advice
   * given before the engagement last changed is about a state that no longer exists, so it is
   * shown as out of date and never used to mark a button.
   */
  const runs = useAgentRuns(engagement.id);
  const advice = latestRun(runs.data?.items, ['PROPOSE_NEXT_STAGE', 'SUGGEST_TRIAGE']);
  const stale = advice ? isStale(advice, engagement) : false;
  const suggestedMove = advice?.result && !stale ? advice.result.proposedNextStage : undefined;

  const move = useMutation({
    mutationFn: (to: EngagementStage) =>
      api.transitionEngagement(engagement.id, { from: engagement.stage, to }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['engagement', engagement.id] });
      await queryClient.invalidateQueries({ queryKey: ['engagements'] });
      // Entering a gate puts the engagement in the approvals queue.
      await queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // Submitting for the Report gate generates the report.
      await queryClient.invalidateQueries({ queryKey: ['reports', engagement.id] });
    },
  });

  if (moves.length === 0) {
    const waiting = WAITING[engagement.stage];
    return waiting ? <p className="next-step muted">{waiting}</p> : null;
  }

  return (
    <div className="next-step">
      <span className="next-step-title">Next step</span>

      {advice?.result ? (
        <div className={`ai-advice ${stale ? 'is-stale' : ''}`}>
          <div className="ai-advice-head">
            <span className="ai-tag">AI suggestion</span>
            {stale ? (
              <span className="muted" style={{ fontSize: 12 }}>
                Out of date — the engagement has changed since. Ask again for current advice.
              </span>
            ) : (
              <ScoreBadge score={advice.result.confidence} />
            )}
          </div>
          {!stale ? (
            <p className="ai-advice-body">
              <strong>
                {advice.result.proposedNextStage
                  ? actionLabel(engagement.stage, advice.result.proposedNextStage)
                  : 'Not ready to move on'}
              </strong>{' '}
              — {advice.result.rationale}
            </p>
          ) : null}
          <p className="ai-advice-note">
            Advice only. You decide; nothing moves until you press a button.
          </p>
        </div>
      ) : null}

      <div className="next-step-actions">
        {moves.map((to) => {
          const blockers = transitionBlockers(engagement.stage, to, evidence);
          return (
            <div key={to} className="next-step-action">
              {/*
               * The same style for every choice: they are alternatives, and drawing one as the
               * primary action nudged people towards it before they knew what either did.
               */}
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={!mayAct || blockers.length > 0 || move.isPending}
                aria-describedby={`next-${to}`}
                onClick={() => move.mutate(to)}
              >
                {move.isPending && move.variables === to
                  ? 'Moving…'
                  : actionLabel(engagement.stage, to)}
              </button>
              {/* Never point at a button that cannot be pressed: the rule beats the advice. */}
              {suggestedMove === to && blockers.length === 0 ? (
                <span className="ai-tag">AI suggests this</span>
              ) : null}
              <p id={`next-${to}`} className="next-step-meaning">
                {actionMeaning(engagement.stage, to)}
              </p>
              {mayAct && blockers.length > 0 ? (
                <div className="next-step-blocked">
                  <span className="next-step-blocked-title">Not available yet</span>
                  <ul className="next-step-blockers">
                    {blockers.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {!mayAct ? (
        <p className="muted" style={{ fontSize: 12 }}>
          Your role can follow this engagement but not move it on.
        </p>
      ) : null}

      {move.error ? (
        <p className="login-error" role="alert">
          {move.error instanceof ApiError
            ? move.error.message
            : 'Could not move this engagement on.'}
        </p>
      ) : null}
    </div>
  );
}
